import { type ReactNode, useEffect, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem, { type MenuItemProps } from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import { misc, neutral } from "app/themes/colors";
import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";
import { getDB } from "features/medical-scribe/medical-scribe-db/medical-scribe-db.util";

import { findLocalRecordingWithAudio } from "./hooks/local-recording.util";
import {
  type ServerAudioDownloadPhase,
  useScribeServerAudioDownload
} from "./hooks/use-scribe-server-audio-download.hook";
import {
  AUDIO_DOWNLOAD_COPY,
  audioUnavailableReason
} from "./scribe-audio-download-button.util";

const MAIN_LABEL: Record<ServerAudioDownloadPhase, string> = {
  idle: AUDIO_DOWNLOAD_COPY.download,
  preparing: AUDIO_DOWNLOAD_COPY.preparing,
  downloading: AUDIO_DOWNLOAD_COPY.downloading
};

// A sibling of the buttons in the player's wrapping row: a full-width line after them, so a
// caption that comes and goes never moves Play or the trailing actions.
const CAPTION_SX = { flexBasis: "100%", order: 1, color: "text.secondary" };

// Fits "Preparing audio…" beside its spinner, so a label change never moves the row.
const BUTTON_MIN_WIDTH = 184;

// The theme's disabled label (neutral[700] on neutral[300]) is 2.6:1; this one is readable.
const BUTTON_SX = {
  minWidth: BUTTON_MIN_WIDTH,
  "&.Mui-disabled": { color: neutral[800] }
};

/** Whether this browser kept a copy of the recording, or `null` while it looks. */
function useHasLocalCopy(
  remoteMedicalScribeId: number | undefined,
  localId: string | undefined
): boolean | null {
  const lookupKey = `${remoteMedicalScribeId ?? ""}:${localId ?? ""}`;
  const [result, setResult] = useState<{ key: string; has: boolean } | null>(
    null
  );

  useEffect(() => {
    let isCurrent = true;
    getDB()
      .then((db) =>
        findLocalRecordingWithAudio(db, remoteMedicalScribeId, localId)
      )
      .then(
        (recording) => recording !== null,
        // A browser that blocks IndexedDB has no copy to offer.
        () => false
      )
      .then((has) => {
        if (isCurrent) setResult({ key: lookupKey, has });
      });
    return () => {
      isCurrent = false;
    };
  }, [lookupKey, remoteMedicalScribeId, localId]);

  return result?.key === lookupKey ? result.has : null;
}

function localItemNote(hasLocalCopy: boolean | null) {
  if (hasLocalCopy === null) return AUDIO_DOWNLOAD_COPY.checking;
  return hasLocalCopy
    ? AUDIO_DOWNLOAD_COPY.localCopyNote
    : AUDIO_DOWNLOAD_COPY.noLocalCopy;
}

type AudioMenuItemProps = Omit<MenuItemProps, "children"> & {
  icon: ReactNode;
  primary: string;
  secondary: string;
};

/**
 * One option of the arrow's menu, with what it downloads (or why it can't) on a second line. The
 * rest props reach `MenuItem`: the menu list clones its first enabled child with `autoFocus` and
 * `tabIndex`, so they must not stop here.
 */
function AudioMenuItem({
  icon,
  primary,
  secondary,
  disabled,
  ...menuItemProps
}: Readonly<AudioMenuItemProps>) {
  return (
    <MenuItem
      {...menuItemProps}
      disabled={disabled}
      // MUI dims a disabled item to 0.38, which would leave its reason unreadable.
      sx={{ maxWidth: 360, "&.Mui-disabled": { opacity: 1 } }}
    >
      <ListItemIcon sx={disabled ? { color: "text.disabled" } : undefined}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{
          color: disabled ? "text.secondary" : "text.primary"
        }}
        secondaryTypographyProps={{
          color: "text.secondary",
          sx: { whiteSpace: "normal" }
        }}
      />
    </MenuItem>
  );
}

type ScribeAudioDownloadButtonProps = {
  remoteMedicalScribeId: number | undefined;
  localId: string | undefined;
  remoteRecordingUrl: string | undefined;
  /** FE status, used only to explain why there is nothing to download. */
  status: number | undefined;
  disabled?: boolean;
};

/**
 * Audio Record Details' download button with AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD on (SN-19937).
 *
 * With audio on the server it is a split button: the main click downloads the whole recording
 * (an MP3, else the original). The arrow lists that same download first, so a disabled local item
 * never reads as "no audio was saved", then the copy this browser saved while recording, disabled
 * with a reason when there is none. With only a local copy it is one button for that
 * copy, and with neither it is disabled and says why. Built like `NoahSessionSplitButton`, not on
 * `components/split-button`, whose unportaled menu has no Escape or keyboard support.
 *
 * Renders a fragment: its pieces are items of the player's button row.
 */
export default function ScribeAudioDownloadButton({
  remoteMedicalScribeId,
  localId,
  remoteRecordingUrl,
  status,
  disabled
}: Readonly<ScribeAudioDownloadButtonProps>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const hasLocalCopy = useHasLocalCopy(remoteMedicalScribeId, localId);
  const { downloadServerAudio, downloadLocalAudio, phase } =
    useScribeServerAudioDownload({ remoteMedicalScribeId, localId });
  const isBusy = phase !== "idle";

  if (!remoteMedicalScribeId || !remoteRecordingUrl) {
    const canDownloadLocal = hasLocalCopy === true;
    let caption: string = AUDIO_DOWNLOAD_COPY.localOnlyCaption;
    if (hasLocalCopy === null) caption = AUDIO_DOWNLOAD_COPY.checking;
    else if (!canDownloadLocal) caption = audioUnavailableReason(status);
    return (
      <>
        <LoadingButton
          variant="reportDownload"
          size="small"
          loading={isBusy}
          loadingPosition="start"
          startIcon={<FileDownloadIcon />}
          disabled={disabled || isBusy || !canDownloadLocal}
          onClick={downloadLocalAudio}
          sx={BUTTON_SX}
        >
          {MAIN_LABEL[phase]}
        </LoadingButton>
        <Typography variant="body1" sx={CAPTION_SX}>
          {caption}
        </Typography>
      </>
    );
  }

  const isLocalItemDisabled = isBusy || hasLocalCopy !== true;

  return (
    <>
      <ButtonGroup
        variant="contained"
        disableElevation
        aria-label="Download audio"
        disabled={disabled}
      >
        <LoadingButton
          variant="reportDownload"
          size="small"
          loading={isBusy}
          loadingPosition="start"
          startIcon={<FileDownloadIcon />}
          disabled={disabled || isBusy}
          onClick={downloadServerAudio}
          sx={{
            ...BUTTON_SX,
            // `&&` because the group's two-class radius and min width beat a one-class `sx`.
            "&&": {
              minWidth: BUTTON_MIN_WIDTH,
              borderRadius: "8px 0 0 8px",
              borderRight: "none"
            }
          }}
        >
          {MAIN_LABEL[phase]}
        </LoadingButton>
        <Button
          variant="reportDownload"
          size="small"
          aria-label={AUDIO_DOWNLOAD_COPY.moreOptions}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          disabled={disabled}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{
            "&&": {
              px: 0,
              minWidth: 48,
              borderRadius: "0 8px 8px 0",
              borderLeft: `1px solid ${misc.starGold}`
            }
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      {phase === "preparing" && (
        <Typography variant="body1" sx={CAPTION_SX} role="status">
          {AUDIO_DOWNLOAD_COPY.preparingCaption}
        </Typography>
      )}
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // Above the scribe modal (snackbar + 101); MUI's default 1300 opens underneath it.
        sx={(theme) => ({
          zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar)
        })}
      >
        <AudioMenuItem
          icon={<CloudDownloadOutlinedIcon />}
          primary={AUDIO_DOWNLOAD_COPY.serverItem}
          secondary={AUDIO_DOWNLOAD_COPY.serverItemNote}
          // A second start would not join the run in flight: it would begin a new conversion.
          disabled={isBusy}
          onClick={() => {
            setAnchor(null);
            downloadServerAudio();
          }}
        />
        <AudioMenuItem
          icon={<ComputerOutlinedIcon />}
          primary={AUDIO_DOWNLOAD_COPY.localItem}
          secondary={localItemNote(hasLocalCopy)}
          disabled={isLocalItemDisabled}
          onClick={() => {
            setAnchor(null);
            downloadLocalAudio();
          }}
        />
      </Menu>
    </>
  );
}
